import React, { useEffect, useMemo, useState } from 'react';

import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { keys } from 'ramda';
import { useCheckboxOptionLabelDbSync } from 'src/components/variables/document-variables/helpers';
import {
  useEditorFindStateActions,
  useEditorFindStateSelector,
} from 'src/editor/editor-find-state-registry';
import {
  EditorTabStateProvider,
  useBindEditorTabStateToDocument,
} from 'src/editor/editor-tab-state-registry';
import { EditorUiStateProvider } from 'src/editor/editor-ui-state-registry';
import { domElIdCreators, useDocumentParams } from 'src/helpers';
import { createHotkeys } from 'src/helpers/hotkeys';
import {
  AuthorizedTransformsProvider,
  useCreateWrappedTransforms,
  useEditorUpdateState,
  useSetActiveComment,
  useSetEditorUserState,
  useSubscribeToDocumentUpdates,
  useSubscribeToPlaybookGenerationUpdates,
  useSubscribeToQuestionGenerationUpdates,
  useSyncUserEditingPrivileges,
} from 'src/hooks';
import { getIsInputElement } from 'src/layout-engine/components/FindPopper';
import { useCurrentTabIdOrThrow } from 'src/layouts/tabs/tab-active-context';

import type { AlineClientEditor } from '../hooks';
import { EditorOnChangeCounterContext } from '../hooks';
import { EDITOR_FOCUS, EDITOR_TO_TAB_STATE } from '../utils/weak-maps';

/**
 * Start count at 1 to ensure Boolean(docContentChangedCount) resolves to true
 * on first render. This is a debounced variable that is used to trigger longer
 * running operations on the document when the document changes, and the editor
 * is idle for 1 second.
 */
export const docContentChangedCountVar = makeVar<number>(1);

export function useDocContentChangedCount() {
  const docContentChangedCount = useReactiveVar(docContentChangedCountVar);
  return { docContentChangedCount };
}

/**
 * Throttled companion to docContentChangedCountVar that ticks only when an op
 * touches suggestion data (suggestion markers or `suggestedEdits`). Lets the
 * suggestions toolbar wake up and update its count during a streaming redline
 * pass instead of waiting for the 1-second `docContentChangedCountVar`
 * debounce to settle. Bumped from `withReact` on a leading+trailing throttle.
 */
export const suggestionContentChangedCountVar = makeVar<number>(1);

export function useSuggestionContentChangedCount() {
  const suggestionContentChangedCount = useReactiveVar(suggestionContentChangedCountVar);
  return { suggestionContentChangedCount };
}

export function Slate(props: {
  children: React.ReactNode;
  editor: AlineClientEditor;
  editorLoading?: boolean;
  isReadOnly?: boolean;
}): JSX.Element {
  const { children, editor, isReadOnly = false } = props;
  const tabId = useCurrentTabIdOrThrow();
  const { documentId } = useDocumentParams();
  const [editorIsFocused, setFocused] = useState(false);
  const [windowIsFocused, setWindowIsFocused] = useState(true);
  const AuthorizedTransforms = useCreateWrappedTransforms(editor);
  const { editorOnChangeCounter, selection } = useEditorUpdateState(editor);
  const uiState = useMemo(
    () => ({
      focused: editorIsFocused && windowIsFocused,
      selection,
    }),
    [editorIsFocused, selection, windowIsFocused]
  );

  useBindEditorTabStateToDocument(tabId, documentId);
  useCheckboxOptionLabelDbSync({ documentId, editor, editorOnChangeCounter });
  useRegisterKeyDownHandlers();
  useSyncUserEditingPrivileges();
  useSetActiveComment(editor, tabId);
  useSubscribeToPlaybookGenerationUpdates();
  useSubscribeToDocumentUpdates(documentId);
  useSubscribeToQuestionGenerationUpdates();
  useSetEditorUserState(editor, { isReadOnly });

  EDITOR_FOCUS.set(editor, { isFocused: editorIsFocused, setFocused });
  EDITOR_TO_TAB_STATE.set(editor, { tabId });

  useEffect(() => {
    function handleFocus() {
      setWindowIsFocused(true);
    }

    function handleBlur() {
      setWindowIsFocused(false);
    }

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  return (
    <EditorTabStateProvider tabId={tabId}>
      <AuthorizedTransformsProvider AuthorizedTransforms={AuthorizedTransforms} tabId={tabId}>
        <EditorUiStateProvider tabId={tabId} uiState={uiState}>
          <EditorOnChangeCounterContext.Provider value={editorOnChangeCounter}>
            {children}
          </EditorOnChangeCounterContext.Provider>
        </EditorUiStateProvider>
      </AuthorizedTransformsProvider>
    </EditorTabStateProvider>
  );
}

export const slateHotkeys = createHotkeys<{
  findActions: ReturnType<typeof useEditorFindStateActions>;
  search: string | null;
}>()({
  search: {
    action: ({ findActions, search }) => {
      const searchInput = document.getElementById(domElIdCreators.alineSearchInputEl());
      if (typeof search !== 'string') findActions.setSearch('');
      if (getIsInputElement(searchInput)) searchInput.select();
    },
    apple: { hint: '⌘F' },
    key: 'mod+f',
    pc: { hint: 'Ctrl+F' },
  },
});

const slateHotkeyKeys = keys(slateHotkeys);

/**
 * This hook registers keydown handlers that we want to fire regardless of if the Editor is focused.
 */
function useRegisterKeyDownHandlers(): void {
  const findActions = useEditorFindStateActions();
  const search = useEditorFindStateSelector(state => state.search);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      for (const key of slateHotkeyKeys) {
        const hotkey = slateHotkeys[key];
        if (hotkey.action && hotkey.test?.(event)) {
          event.preventDefault();
          hotkey.action({ findActions, search });
          break;
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [findActions, search]);
}
